import React, { useEffect, useState } from "react";
import { marketingApi, fmtUsd, fmtInt, fmtPct } from "../marketingApi";
import { Kpi, Section, EmptyState, Loading } from "../components";
import Last30dWidget from "../Last30dWidget";

export default function OverviewTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    marketingApi.overview().then(setData).catch((e) => setErr(e?.response?.data?.detail || "Error"));
  }, []);
  if (err) return <EmptyState title="Error al cargar" description={err} testId="overview-error" />;
  if (!data) return <Loading />;

  const f = data.funnel || {};
  const totals = data.profit_totals || {};
  const sources = data.top_sources || [];
  const rows = data.profit_rows_top || [];

  return (
    <div className="space-y-6" data-testid="overview-tab">
      {/* Premium 30-day widget (GA4 + GSC, real data only) */}
      <Last30dWidget />

      {/* Funnel KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi testId="kpi-pageviews" label="Pageviews" value={fmtInt(f.pageviews)} hint="Vistas de /join" />
        <Kpi testId="kpi-leads" label="Leads únicos" value={fmtInt(f.leads)} hint="Sesiones distintas" />
        <Kpi testId="kpi-submits" label="Envíos" value={fmtInt(f.submits)} hint="Formularios completados" />
        <Kpi testId="kpi-conversions" label="Conversiones" value={fmtInt(f.conversions)} hint="Lead → participante" />
        <Kpi testId="kpi-revenue" label="Ingreso atribuido" value={fmtUsd(f.revenue_usd)} tone="primary" hint="A leads trackeados" />
      </div>

      {/* Profit totals + sources */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Rentabilidad agregada" subtitle="Suma de todos los tours activos. Calculado con las mismas fórmulas del módulo Gastos & Rentabilidad.">
          <div className="grid grid-cols-2 gap-3">
            <Kpi testId="kpi-rev-total" label="Ingresos previstos" value={fmtUsd(totals.revenue_total)} />
            <Kpi testId="kpi-budget-total" label="Gastos presupuestados" value={fmtUsd(totals.budgeted_total)} />
            <Kpi testId="kpi-margin-total" label="Margen bruto previsto" value={fmtUsd(totals.margin_total)} />
            <Kpi testId="kpi-net-total" label="Cobrado neto" value={fmtUsd(totals.net_collected_total)} />
          </div>
          <p className="text-[11px] text-slate-500 mt-3">
            Si un tour carece de precios o costes reales, aparece como <b>Datos insuficientes</b> en la pestaña Rentabilidad y no se incluye en el total.
          </p>
        </Section>

        <Section title="Top fuentes" subtitle="Agrupado por utm_source · utm_medium">
          {sources.length === 0 ? (
            <EmptyState
              title="Aún no hay fuentes registradas"
              description="Comparte /join con parámetros UTM (?utm_source=…&utm_medium=…) para empezar a medir."
              testId="sources-empty"
            />
          ) : (
            <div className="overflow-x-auto -mx-5 sm:mx-0">
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 uppercase tracking-wider">
                  <tr className="border-b border-slate-200 dark:border-[#25334D]"><th className="py-2 px-3 text-left">Fuente</th><th className="py-2 px-3 text-left">Medio</th><th className="py-2 px-3 text-right">Leads</th><th className="py-2 px-3 text-right">Envíos</th></tr>
                </thead>
                <tbody>
                  {sources.map((s, i) => (
                    <tr key={i} className="border-b border-slate-100 dark:border-[#1A2336]"><td className="py-2 px-3 font-medium">{s.source}</td><td className="py-2 px-3 text-slate-500">{s.medium}</td><td className="py-2 px-3 text-right">{fmtInt(s.leads)}</td><td className="py-2 px-3 text-right">{fmtInt(s.submitted)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      </div>

      {/* Top tours by profit */}
      <Section title="Top expediciones por margen previsto" subtitle="Las 5 primeras ordenadas por fecha de inicio">
        {rows.length === 0 ? (
          <EmptyState title="No hay tours disponibles" description="Crea tus primeras expediciones en el módulo Expediciones." />
        ) : (
          <div className="overflow-x-auto -mx-5 sm:mx-0">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase tracking-wider">
                <tr className="border-b border-slate-200 dark:border-[#25334D]"><th className="py-2 px-3 text-left">Expedición</th><th className="py-2 px-3 text-left">País</th><th className="py-2 px-3 text-right">PAX</th><th className="py-2 px-3 text-right">Ingresos</th><th className="py-2 px-3 text-right">Gastos</th><th className="py-2 px-3 text-right">Margen</th><th className="py-2 px-3 text-right">%</th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.tour_id} className="border-b border-slate-100 dark:border-[#1A2336]">
                    <td className="py-2 px-3 font-medium">{r.tour_name || "—"}</td>
                    <td className="py-2 px-3 text-slate-500">{r.country || "—"}</td>
                    <td className="py-2 px-3 text-right">{fmtInt(r.pax)}</td>
                    <td className="py-2 px-3 text-right">{r.expected_revenue === null ? <span className="text-amber-600 text-xs">Datos insuficientes</span> : fmtUsd(r.expected_revenue)}</td>
                    <td className="py-2 px-3 text-right">{fmtUsd(r.budgeted_expenses)}</td>
                    <td className="py-2 px-3 text-right font-semibold">{r.gross_margin_expected === null ? "—" : fmtUsd(r.gross_margin_expected)}</td>
                    <td className="py-2 px-3 text-right text-slate-500">{fmtPct(r.margin_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
